import { UseMutationResult, useMutation, useQuery } from '@tanstack/react-query'
import {
  createBundleCondition,
  createBundleConditionSku,
  deleteBundleConditions,
  getSingleBatchSettings,
  updateBundleConditionStatus,
  uploadBundleConditionsExcel,
  uploadBundleConditionsSkuExcel,
} from '@/services/outbound/singleBatchSetting/apis'
import {
  CreateBundleConditionSkuReq,
  GetSingleBatchSettingsReq,
  UploadBundleConditionsExcelErrorData,
  UploadBundleConditionsExcelReq,
} from '@/services/outbound/singleBatchSetting/types'
import useExcelDownload from '@/hooks/useDownloadExcel'
import { ExcelDownloadType } from '@/services/excel/excel.types'

const PATHS = ['OUTBOUND', 'SINGLE_BATCH_SETTING']
const GET_BUNDLE_CONDITIONS = [...PATHS, 'GET_BUNDLE_CONDITIONS']

export const useGetSingleBatchSettings = (params: GetSingleBatchSettingsReq, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: [...GET_BUNDLE_CONDITIONS, params],
    queryFn: () => getSingleBatchSettings(params),
    select: ({ data }) => data,
    enabled: options?.enabled ?? true,
  })
}

const GET_BUNDLE_CONDITION_BY_WAREHOUSE = [...GET_BUNDLE_CONDITIONS, 'BY_WAREHOUSE']

export const useGetBundleConditionByWarehouse = (warehouseCode?: string) => {
  return useQuery({
    queryKey: [...GET_BUNDLE_CONDITION_BY_WAREHOUSE, warehouseCode],
    queryFn: () => getSingleBatchSettings({ warehouseCode, page: 0, size: 1 }),
    select: ({ data }) => data.items[0],
    enabled: !!warehouseCode,
  })
}

export const useExportBundleConditionsExcel = () => {
  const excelDownload = useExcelDownload({ fileNamePrefix: 'bundle_conditions' })
  return {
    isLoading: excelDownload.isLoading,
    // 사유는 BE @AccessAudit 이 쿼리에서 읽어 감사로그에 남긴다 — 자유 입력이라 인코딩해서 싣는다
    onExportData: (id: number, { reasonCode, detailedReason }: ExcelDownloadType) =>
      excelDownload.onDownloadExcel(`/api/v1/bundle-conditions/${id}/excel?${new URLSearchParams({ reasonCode, detailedReason })}`),
  }
}

export const useCreateBundleCondition = () => useMutation({ mutationFn: createBundleCondition })

export const useCreateBundleConditionSku = (): UseMutationResult<ApiResponseV2<null>, Error, CreateBundleConditionSkuReq> =>
  useMutation({ mutationFn: createBundleConditionSku })

export const useUploadBundleConditionsExcel = () => useMutation({ mutationFn: uploadBundleConditionsExcel })

export const useUploadBundleConditionsSkuExcel = (): UseMutationResult<
  ApiResponseV2<UploadBundleConditionsExcelErrorData | null>,
  Error,
  UploadBundleConditionsExcelReq
> => useMutation({ mutationFn: uploadBundleConditionsSkuExcel })

export const useDeleteBundleConditions = () => useMutation({ mutationFn: deleteBundleConditions })

export const useUpdateBundleConditionStatus = () => useMutation({ mutationFn: updateBundleConditionStatus })
